import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, ExternalLink } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { tooltipStyle } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

const latest = (rows) => (rows?.length ? rows[rows.length - 1] : null);
const fmt1 = (v) => (v == null ? "—" : dec(v, 1));

const Forsorgerbyrde = ({ data, fylkeName, storby }) => {
  const k = latest(data.kommune);
  if (!k?.dep_total) return null;
  const f = data.fylke?.find((r) => r.year === k.year);
  const l = data.land?.find((r) => r.year === k.year);
  const series = [["Kommunen", k, "#103766"], [fylkeName || "Fylket", f, "#3F8A86"], ["Landet", l, "#9CA3AF"]].filter(([, r]) => r);
  const chart = series.map(([name, r]) => ({ name, "Barn og unge 0–19 år": r.dep_young, "Eldre 67 år og over": r.dep_old, total: r.dep_total }));
  return (
    <div data-testid="forsorgerbyrde">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Barn og eldre per 100 yrkesaktive {k.year}</p>
      <p className="text-sm text-[#4B647F] mb-4">
        Tal på barn/unge (0–19 år) og eldre (67+) per 100 innbyggjarar i yrkesaktiv alder (20–66 år). Høgare tal tyder fleire barn og eldre i forhold til dei yrkesaktive.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-3 gap-2 content-start">
          {series.map(([name, r], i) => (
            <div key={name} className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={`forsorgerbyrde-${i === 0 ? "kommune" : i === series.length - 1 ? "land" : "fylke"}`}>
              <p className="text-[11px] text-[#8AA4BF]">{name}</p>
              <p className="text-2xl font-light text-[#1F2A36]">{fmt1(r.dep_total)}</p>
              <p className="text-[11px] text-[#4B647F]">per 100 yrkesaktive</p>
              <p className="text-[11px] text-[#4B647F] mt-1">Barn/unge {fmt1(r.dep_young)} · Eldre {fmt1(r.dep_old)}</p>
            </div>
          ))}
          <p className="col-span-3 text-xs text-[#4B647F]" data-testid="forsorgerbyrde-diff">
            {l && (k.dep_total >= l.dep_total
              ? `Kommunen har ${dec((k.dep_total - l.dep_total), 1)} fleire barn og eldre per 100 yrkesaktive enn landet`
              : `Kommunen har ${dec((l.dep_total - k.dep_total), 1)} færre barn og eldre per 100 yrkesaktive enn landet`)}
            {f && ` og ${k.dep_total >= f.dep_total ? dec((k.dep_total - f.dep_total), 1) + " fleire" : dec((f.dep_total - k.dep_total), 1) + " færre"} enn ${storby ? "storbysnittet" : "fylkessnittet"}`}.
          </p>
        </div>
        <div className="h-[260px]" data-testid="forsorgerbyrde-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} />
              <Tooltip formatter={(v) => `${fmt1(v)} per 100`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} />
              <Bar dataKey="Barn og unge 0–19 år" stackId="a" fill="#3F8A86" />
              <Bar dataKey="Eldre 67 år og over" stackId="a" fill="#E8A34A" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="total" position="top" formatter={fmt1} fontSize={12} fill="#1F2A36" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const AldringFramover = ({ data, fylkeName }) => {
  const fr = data.framskriving;
  const k = latest(data.kommune);
  if (!fr?.kommune?.length || !k) return null;
  const at = (rows, y) => rows?.find((r) => r.year === y)?.mean_age ?? null;
  const years = [...new Set([...data.kommune, ...fr.kommune, ...(data.land || []), ...(fr.land || [])].map((r) => r.year))].sort();
  const chart = years.map((y) => ({
    year: y,
    Kommunen: y <= k.year ? at(data.kommune, y) : null,
    "Kommunen (framskrive)": y >= k.year ? at(fr.kommune, y) ?? (y === k.year ? at(data.kommune, y) : null) : null,
    ...(data.fylke?.length ? { Fylket: y <= k.year ? at(data.fylke, y) : null, "Fylket (framskrive)": y >= k.year ? at(fr.fylke, y) : null } : {}),
    Landet: y <= k.year ? at(data.land, y) : null,
    "Landet (framskrive)": y >= k.year ? at(fr.land, y) : null,
  }));
  const fk = (y) => fr.kommune.find((r) => r.year === y);
  const milestones = [2030, 2040, 2050].map((y) => [y, fk(y)]).filter(([, r]) => r);
  return (
    <div data-testid="aldring-framover">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Aldring framover: venta gjennomsnittsalder til 2050</p>
      <p className="text-sm text-[#4B647F] mb-4">
        Heltrukne linjer er faktisk utvikling, stipla linjer er SSBs hovudalternativ (MMMM) i 2024-framskrivinga.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 content-start">
          {milestones.map(([y, r]) => (
            <div key={y} className="p-3 border border-[#E6E9F0] rounded-lg" data-testid={`aldring-${y}`}>
              <p className="text-[11px] text-[#8AA4BF]">{y}</p>
              <p className="text-2xl font-light text-[#1F2A36]">{fmt1(r.mean_age)} år</p>
              <p className="text-[11px] text-[#4B647F]">{r.mean_age - k.mean_age >= 0 ? "+" : ""}{dec((r.mean_age - k.mean_age), 1)} år frå {k.year} · barn og eldre per 100: {fmt1(r.dep_total)}</p>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 h-[320px]" data-testid="aldring-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart}>
              {endringsLinjer(chart)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} ticks={years.filter((y) => y % 5 === 0 || y === k.year)} />
              <YAxis domain={["dataMin - 1", "dataMax + 1"]} stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => v.toFixed(0)} width={30} />
              <Tooltip formatter={(v) => `${fmt1(v)} år`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine x={k.year} stroke="#8AA4BF" strokeDasharray="2 2" label={{ value: "i dag", position: "top", fontSize: 11, fill: "#8AA4BF" }} />
              <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={2.5} dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="Kommunen (framskrive)" stroke="#103766" strokeWidth={2.5} strokeDasharray="6 4" dot={false} legendType="none" />
              {data.fylke?.length > 0 && <Line type="monotone" dataKey="Fylket" stroke="#3F8A86" strokeWidth={2} dot={false} />}
              {data.fylke?.length > 0 && <Line type="monotone" dataKey="Fylket (framskrive)" stroke="#3F8A86" strokeWidth={2} strokeDasharray="6 4" dot={false} legendType="none" />}
              <Line type="monotone" dataKey="Landet" stroke="#9CA3AF" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Landet (framskrive)" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="6 4" dot={false} legendType="none" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export const AlderSection = ({ data }) => {
  if (!data?.kommune?.length) return null;
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="alder-section" id="alder">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Users className="w-5 h-5 text-[#103766]" />
          Alderssamansetjing: barn og eldre
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Kor mange barn og eldre er det per 100 i yrkesaktiv alder, og kor raskt eldest befolkninga?</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-10">
        <Forsorgerbyrde data={data} fylkeName={data.fylke_name} storby={!!data.gruppe_namn} />
        <AldringFramover data={data} fylkeName={data.fylke_name} />
        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB <a href="https://www.ssb.no/statbank/table/07459" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">07459 <ExternalLink className="w-3 h-3" /></a> (befolkning etter alder) og{" "}
          <a href="https://www.ssb.no/statbank/table/14288" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">14288 <ExternalLink className="w-3 h-3" /></a> (framskrive folkemengde, hovudalternativet). 105+ blir rekna som 105 år.
        </p>
      </CardContent>
    </Card>
  );
};

export default AlderSection;
